import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, Alert, Chip, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Tooltip, LinearProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Clear as ClearIcon,
  CheckCircle as OkIcon,
  Warning as WarnIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

/**
 * Reusable bulk uploader for price-tier items. Parses CSV / .xlsx / .xls,
 * normalises headers (case-insensitive), validates each row, shows a
 * preview, and exposes the cleaned rows via onChange(rows).
 *
 * Props:
 *   onChange(rows)  — array of { sku, unit_price, notes }, called whenever
 *                     the parsed+valid set changes (including when cleared)
 *   currency        — display-only, used in the example download
 *   compact         — render with less vertical padding (when embedded in a
 *                     dialog alongside other fields)
 */
const EXAMPLE_CSV = `sku,unit_price,notes
SS001-NAT-30ML,29.50,
SS002-NAT-50ML,42.00,Available May 2026 onwards
SS003-HYD-100ML,55.00,`;

const HEADER_ALIASES = {
  sku: ['sku', 'product_sku', 'item', 'item_sku', 'code'],
  unit_price: ['unit_price', 'price', 'wholesale_price', 'unit price', 'wholesale price'],
  notes: ['notes', 'note', 'comment', 'comments'],
};

function findHeader(headers, target) {
  const aliases = HEADER_ALIASES[target];
  const lower = headers.map(h => String(h || '').toLowerCase().trim());
  for (const alias of aliases) {
    const i = lower.indexOf(alias);
    if (i !== -1) return headers[i];
  }
  return null;
}

function validateRow(row, idx, seenSkus) {
  const sku = String(row.sku || '').trim();
  const priceRaw = row.unit_price;
  const priceNum = typeof priceRaw === 'number'
    ? priceRaw
    : parseFloat(String(priceRaw || '').replace(/[$,£€]/g, '').trim());

  if (!sku) return { level: 'error', label: 'Missing SKU' };
  if (!Number.isFinite(priceNum) || priceNum < 0) return { level: 'error', label: 'Invalid price' };
  if (seenSkus.has(sku.toLowerCase())) return { level: 'warning', label: 'Duplicate SKU' };
  return { level: 'ok', label: 'OK' };
}

export default function PriceTierBulkUpload({ onChange, currency = '', compact = false }) {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [rawRows, setRawRows] = useState([]); // [{sku, unit_price, notes}, ...]
  const [dragOver, setDragOver] = useState(false);

  const validated = useMemo(() => {
    const seen = new Set();
    return rawRows.map((row, idx) => {
      const v = validateRow(row, idx, seen);
      if (v.level !== 'error') seen.add(String(row.sku || '').toLowerCase().trim());
      return { ...row, _validation: v };
    });
  }, [rawRows]);

  const stats = useMemo(() => ({
    total: validated.length,
    ok: validated.filter(r => r._validation.level === 'ok').length,
    warn: validated.filter(r => r._validation.level === 'warning').length,
    err: validated.filter(r => r._validation.level === 'error').length,
  }), [validated]);

  // Bubble cleaned, error-free rows up. On duplicate, last occurrence wins
  // (matches the natural "edit in spreadsheet" mental model).
  React.useEffect(() => {
    const map = new Map();
    for (const r of validated) {
      if (r._validation.level === 'error') continue;
      const sku = String(r.sku).trim();
      map.set(sku.toLowerCase(), {
        sku,
        unit_price: typeof r.unit_price === 'number'
          ? r.unit_price
          : parseFloat(String(r.unit_price).replace(/[$,£€]/g, '').trim()) || 0,
        notes: String(r.notes || '').trim(),
      });
    }
    onChange?.(Array.from(map.values()));
  }, [validated, onChange]);

  const parseRowsFromObjects = (objects) => {
    if (!Array.isArray(objects) || objects.length === 0) {
      setParseError('File has no data rows');
      setRawRows([]);
      return;
    }
    const headers = Object.keys(objects[0] || {});
    const skuHeader = findHeader(headers, 'sku');
    const priceHeader = findHeader(headers, 'unit_price');
    const notesHeader = findHeader(headers, 'notes');
    if (!skuHeader || !priceHeader) {
      setParseError(`Required headers not found. Need at least "sku" and "unit_price". Got: ${headers.join(', ')}`);
      setRawRows([]);
      return;
    }
    setParseError(null);
    setRawRows(objects.map(o => ({
      sku: o[skuHeader],
      unit_price: o[priceHeader],
      notes: notesHeader ? o[notesHeader] : '',
    })));
  };

  const handleFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    setParseError(null);
    setRawRows([]);

    const ext = file.name.toLowerCase().split('.').pop();
    if (ext === 'csv' || ext === 'txt') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (res) => {
          setParsing(false);
          if (res.errors?.length) {
            setParseError(`Parse error: ${res.errors[0].message}`);
            return;
          }
          parseRowsFromObjects(res.data);
        },
        error: (err) => {
          setParsing(false);
          setParseError(`Parse error: ${err.message}`);
        },
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          if (!ws) { setParseError('Workbook has no sheets'); setParsing(false); return; }
          const objects = XLSX.utils.sheet_to_json(ws, { defval: '' });
          parseRowsFromObjects(objects);
        } catch (err) {
          setParseError(`Excel parse error: ${err.message}`);
        } finally {
          setParsing(false);
        }
      };
      reader.onerror = () => { setParseError('Failed to read file'); setParsing(false); };
      reader.readAsArrayBuffer(file);
    } else {
      setParsing(false);
      setParseError('Unsupported file type. Use .csv, .xlsx, or .xls');
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleClear = () => {
    setRawRows([]);
    setFileName('');
    setParseError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadExample = () => {
    const blob = new Blob([EXAMPLE_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'price-tier-example.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderChip = (v) => {
    if (v.level === 'ok') return <Chip size="small" icon={<OkIcon fontSize="inherit" />} label="OK" color="success" variant="outlined" />;
    if (v.level === 'warning') return <Chip size="small" icon={<WarnIcon fontSize="inherit" />} label={v.label} color="warning" variant="outlined" />;
    return <Chip size="small" icon={<ErrorIcon fontSize="inherit" />} label={v.label} color="error" variant="outlined" />;
  };

  const previewRows = validated.slice(0, 50);
  const overflow = Math.max(0, validated.length - previewRows.length);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" color="text.secondary">
          Bulk upload (optional)
        </Typography>
        <Button size="small" startIcon={<DownloadIcon />} onClick={downloadExample}>
          Download example CSV
        </Button>
      </Stack>

      {rawRows.length === 0 ? (
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            bgcolor: dragOver ? 'action.hover' : 'background.default',
            borderRadius: 1,
            p: compact ? 2 : 3,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
          }}
        >
          <UploadIcon sx={{ fontSize: compact ? 28 : 36, color: 'text.secondary', mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            Drop a <strong>CSV</strong> or <strong>Excel</strong> file here, or click to browse
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Columns: <code>sku</code>, <code>unit_price</code>, <code>notes</code> (header row required) — prices in {currency || 'the tier currency (not set yet)'}
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => handleFile(e.target.files?.[0])}
            style={{ display: 'none' }}
          />
        </Box>
      ) : (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Chip size="small" label={fileName} />
            <Chip size="small" label={`${stats.ok} OK`} color="success" variant="outlined" />
            {stats.warn > 0 && <Chip size="small" label={`${stats.warn} duplicate`} color="warning" variant="outlined" />}
            {stats.err > 0 && <Chip size="small" label={`${stats.err} error`} color="error" variant="outlined" />}
            <Box flexGrow={1} />
            <Tooltip title="Clear file">
              <IconButton size="small" onClick={handleClear}><ClearIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: compact ? 220 : 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>#</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell sx={{ width: 140 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{String(r.sku || '')}</TableCell>
                    <TableCell align="right">{String(r.unit_price ?? '')}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{String(r.notes || '')}</TableCell>
                    <TableCell>{renderChip(r._validation)}</TableCell>
                  </TableRow>
                ))}
                {overflow > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      …and {overflow} more row{overflow === 1 ? '' : 's'} below
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {parsing && <LinearProgress sx={{ mt: 1 }} />}
      {parseError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setParseError(null)}>{parseError}</Alert>}
    </Box>
  );
}
